import {useEffect, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {
  Breadcrumb,
  Button,
  Col,
  Form,
  Row,
  Spinner,
} from 'react-bootstrap'
import {useLazyQuery, useQuery, useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {hasExactPermission, hasPermission, parseId, parsePrice} from '../../helpers'
import moment from 'moment'
import {ALL_PERMISSIONS} from '../../helpers/consts'
import {GET_ALL_USERS_FILTER} from '../../gql/queries/usersQuery'
import {IResAllUsers} from '../oneUser/oneUserTypes'
import {GET_ALL_CARRIERS} from '../../gql/queries/carriersQuery'
import {IResCarriers} from '../../types/carriers'
import {GET_PAYROLL_TOTALS} from '../../gql/queries/commissionReportsApiQueries'
import {SEND_PAYROLL_EMAILS} from '../../gql/mutations/commissionReportMutations'
import React from 'react'
import {useGetUser} from '../../store/userStore'
import {ModalConfirmation} from './components/ModalConfirmation'
import {STORAGE_NAMES} from '../../helpers/consts'
import {URL} from '../../gql/client'

// Types for payroll totals
interface PayrollReportTotal {
  reportId: string
  reportName: string
  totalCommission: number
  totalChargeback: number
  totalDeduction: number
  totalBonus: number
  total: number
}

interface PayrollAgentTotal {
  userId: string
  userFirstName: string
  userLastName: string
  userEmail: string
  reports: PayrollReportTotal[]
  grandTotal: number
}

interface IResPayrollTotals {
  commissionReportPayrollTotals: PayrollAgentTotal[]
}

const AGENT_HEADER_COLOR = '#2c5f8d'

const getInitialDateRange = () => {
  const today = moment()
  const day = today.date()
  const start =
    day <= 15
      ? today.clone().startOf('month')
      : today.clone().date(16)
  const end =
    day <= 15
      ? today.clone().date(15)
      : today.clone().endOf('month')

  return {
    from: start.format('YYYY-MM-DD'),
    to: end.format('YYYY-MM-DD'),
  }
}

const agentHeaderRowStyle = {
  backgroundColor: AGENT_HEADER_COLOR,
  color: 'white',
  '--bs-table-bg': AGENT_HEADER_COLOR,
  '--bs-table-accent-bg': AGENT_HEADER_COLOR,
  '--bs-table-hover-bg': AGENT_HEADER_COLOR,
  '--bs-table-striped-bg': AGENT_HEADER_COLOR,
} as React.CSSProperties

const agentHeaderCellStyle = {
  backgroundColor: AGENT_HEADER_COLOR,
  color: 'white',
  textAlign: 'left',
  padding: '8px',
  fontWeight: 'bold',
} as React.CSSProperties

export const CommissionReportsPayrollTotalsPage = () => {
  const permissions = useGetUser((s) => s.user?.permissions)
  const canEditCommission =
    permissions && hasExactPermission(permissions, ALL_PERMISSIONS.EDIT_COMMISSION)
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const currentUser = useGetUser((s) => s.user?.user)
  const currentUserId = currentUser?.id
  const isAgente = userGroups.includes('Agente')
  const navigate = useNavigate()

  // Filtros
  const initialRange = getInitialDateRange()
  const [dateFrom, setDateFrom] = useState<string>(initialRange.from)
  const [dateTo, setDateTo] = useState<string>(initialRange.to)
  const [selectedAgent, setSelectedAgent] = useState<string>('')
  const [selectedCarrier, setSelectedCarrier] = useState<string>('')
  const [isSendEmailsModalOpen, setIsSendEmailsModalOpen] = useState(false)
  const didRunInitialFilter = useRef(false)
  const hasSetAgentFilter = useRef(false)

  // GraphQL Queries
  const {data: dataMembers} = useQuery<IResAllUsers>(GET_ALL_USERS_FILTER, {
    fetchPolicy: 'no-cache',
    variables: {requireNpn: true},
  })

  const {data: dataCarriers} = useQuery<IResCarriers>(GET_ALL_CARRIERS, {
    fetchPolicy: 'no-cache',
  })

  const [
    gqlGetPayrollTotals,
    {data: dataPayrollTotals, loading, error},
  ] = useLazyQuery<IResPayrollTotals>(GET_PAYROLL_TOTALS, {
    fetchPolicy: 'no-cache',
  })

  const [sendPayrollEmails, {loading: sendingEmails}] = useMutation(SEND_PAYROLL_EMAILS)
  const [exportingMatrix, setExportingMatrix] = useState(false)

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])

  const handleSearch = () => {
    gqlGetPayrollTotals({
      variables: {
        dateFrom,
        dateTo,
        userId: selectedAgent ? parseId(selectedAgent) : null,
        carrierId: selectedCarrier ? parseId(selectedCarrier) : null,
      },
    })
  }

  const handleSendEmails = async () => {
    if (!dateFrom || !dateTo) {
      toast.error('Please select date range')
      return
    }

    try {
      const {data} = await sendPayrollEmails({
        variables: {
          dateFrom,
          dateTo,
          userId: selectedAgent ? parseId(selectedAgent) : null,
        },
      })

      const result = data?.sendPayrollEmailsMutation

      if (result?.success) {
        toast.success(result.message || 'Emails sent successfully')
      } else {
        toast.error(result?.errors?.[0]?.message || 'Failed to send emails')
      }
    } catch (err: any) {
      toast.error(`Error: ${err.message}`)
    }
  }

  const handleExportMatrix = async () => {
    if (!dateFrom || !dateTo) {
      toast.error('Please select date range')
      return
    }

    const params = new URLSearchParams()
    params.append('date_from', dateFrom)
    params.append('date_to', dateTo)
    if (selectedAgent) {
      params.append('user_id', parseId(selectedAgent).toString())
    }

    const token = localStorage.getItem(STORAGE_NAMES.TOKEN)
    if (!token) {
      toast.error('No auth token found. Please log in again.')
      return
    }
    const requestOptions: RequestInit = {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }

    setExportingMatrix(true)
    try {
      const url = `${URL}/export-payroll-matrix-excel?${params.toString()}`
      const response = await fetch(url, requestOptions)
      if (!response.ok) throw new Error('Error al exportar')
      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = `payroll-matrix-${moment().format('YY-MM-DD')}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
      toast.success('Excel exported successfully')
    } catch (err) {
      toast.error(`ERROR: ${(err as {message: string}).message}`)
    } finally {
      setExportingMatrix(false)
    }
  }

  const selectedAgentLabel =
    dataMembers?.allUsers.edges.find(({node}) => node.id === selectedAgent)?.node

  const confirmationBody = isAgente && currentUser ? (
    <>
      This will send the payroll email to{' '}
      <strong>
        {currentUser.firstName} {currentUser.lastName} ({currentUser.email})
      </strong>{' '}
      for the selected date range.
    </>
  ) : selectedAgentLabel ? (
    <>
      This will send the payroll email to{' '}
      <strong>
        {selectedAgentLabel.firstName} {selectedAgentLabel.lastName} ({selectedAgentLabel.email})
      </strong>{' '}
      for the selected date range.
    </>
  ) : (
    'This will send payroll emails to all agents with payroll in the selected date range.'
  )

  // Ensure agent filter is set when dataMembers loads
  useEffect(() => {
    if (isAgente && currentUserId && dataMembers && !hasSetAgentFilter.current) {
      setSelectedAgent(currentUserId)
      hasSetAgentFilter.current = true
    }
  }, [isAgente, currentUserId, dataMembers])

  // Load data on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    handleSearch()
  }, [])

  useEffect(() => {
    // Trigger search immediately when agent/carrier filter changes, but skip initial render
    if (!didRunInitialFilter.current) {
      didRunInitialFilter.current = true
      return
    }
    handleSearch()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgent, selectedCarrier])

  const payrollData = dataPayrollTotals?.commissionReportPayrollTotals || []

  // No frontend filtering needed - backend handles it all
  const filteredData = payrollData

  return (
    <div className='policies-page'>
      <header className='header'>
        <Breadcrumb>
          <Breadcrumb.Item className='fs-3' onClick={() => navigate('/commissions')}>
            Reports List
          </Breadcrumb.Item>
          <Breadcrumb.Item className='fs-3'>Payroll Totals</Breadcrumb.Item>
        </Breadcrumb>
      </header>

      <Row className='commissionReports'>
        <Col className='container'>
          <div className='policies-container'>
            <div className='title-header'>
              <h3>Payroll Totals by Agent</h3>
              {!isAgente && (
                <div className='d-flex gap-2'>
                  <Button
                    size='sm'
                    variant='primary'
                    onClick={handleExportMatrix}
                    disabled={exportingMatrix || !dateFrom || !dateTo}
                  >
                    {exportingMatrix ? (
                      <>
                        <Spinner animation='border' size='sm' className='me-2' />
                        Exporting...
                      </>
                    ) : (
                      <>
                        <i className='bi bi-file-earmark-excel' /> Export Matrix Excel
                      </>
                    )}
                  </Button>
                  <Button
                    size='sm'
                    variant='success'
                    onClick={() => setIsSendEmailsModalOpen(true)}
                    disabled={sendingEmails || !dateFrom || !dateTo}
                  >
                    {sendingEmails ? (
                      <>
                        <Spinner animation='border' size='sm' className='me-2' />
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className='bi bi-envelope' /> Send Payroll Emails
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>

            <div className='filters-header mb-3'>
              <Row>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Date From</Form.Label>
                    <Form.Control
                      type='date'
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Date To</Form.Label>
                    <Form.Control
                      type='date'
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                {canEditCommission && (
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Filter by Agent</Form.Label>
                      <Form.Select
                        value={selectedAgent}
                        onChange={(e) => setSelectedAgent(e.target.value)}
                        disabled={isAgente}
                      >
                        <option value=''>All Agents</option>
                        {dataMembers?.allUsers.edges.map(({node}) => (
                          <option key={node.id} value={node.id}>
                            {node.firstName} {node.lastName} - {node.email}
                            {node.npn ? ` (${node.npn})` : ''}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Filter by Carrier</Form.Label>
                    <Form.Select
                      value={selectedCarrier}
                      onChange={(e) => setSelectedCarrier(e.target.value)}
                    >
                      <option value=''>All Carriers</option>
                      {dataCarriers?.allInsuranceOperators.edges.map(({node}) => (
                        <option key={node.id} value={node.id}>
                          {node.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row className='mt-3'>
                <Col>
                  <Button variant='primary' onClick={handleSearch} disabled={loading}>
                    {loading ? (
                      <>
                        <Spinner animation='border' size='sm' className='me-2' />
                        Searching...
                      </>
                    ) : (
                      <>
                        <i className='bi bi-search' /> Search
                      </>
                    )}
                  </Button>
                </Col>
              </Row>
            </div>

            <div className='table-responsive table table-striped table-hover'>
              <table
                className='table-scrollable table-scroll table table-striped table-hover'
                style={{whiteSpace: 'nowrap'}}
              >
                <thead>
                  <tr role='row'>
                    <th className='fw-bold'>Report</th>
                    <th className='fw-bold text-end'>Total Commission</th>
                    <th className='fw-bold text-end'>Total Chargeback</th>
                    <th className='fw-bold text-end'>Total Deductions</th>
                    <th className='fw-bold text-end'>Total Bonus</th>
                    <th className='fw-bold text-end'>Total</th>
                  </tr>
                </thead>
                {loading ? (
                  <tbody>
                    <tr>
                      <td colSpan={6}>
                        <div className='d-flex flex-center' style={{height: '30vh'}}>
                          <Spinner animation='border' />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <tbody>
                    {filteredData.length === 0 && (
                      <tr>
                        <td colSpan={6}>
                          <div className='d-flex flex-center' style={{height: '30vh'}}>
                            No payroll data found for the selected criteria
                          </div>
                        </td>
                      </tr>
                    )}

                    {filteredData.map((agent) => (
                      <React.Fragment key={agent.userId}>
                        <tr className='agent-header-row' style={agentHeaderRowStyle}>
                          <td colSpan={6} style={agentHeaderCellStyle}>
                            Agent: {agent.userFirstName} {agent.userLastName} ({agent.userEmail})
                          </td>
                        </tr>
                        {agent.reports.map((report) => (
                          <tr key={`${agent.userId}-${report.reportId}`}>
                            <td>{report.reportName}</td>
                            <td className='text-end'>{parsePrice(report.totalCommission)}</td>
                            <td className='text-end'>{parsePrice(report.totalChargeback)}</td>
                            <td className='text-end'>{parsePrice(report.totalDeduction)}</td>
                            <td className='text-end'>{parsePrice(report.totalBonus)}</td>
                            <td className='text-end fw-bold'>{parsePrice(report.total)}</td>
                          </tr>
                        ))}
                        <tr>
                          <td
                            colSpan={5}
                            style={{
                              backgroundColor: '#9fddaeff',
                              color: 'black',
                              textAlign: 'right',
                              fontWeight: 'bold',
                            }}
                          >
                            Grand Total for {agent.userFirstName} {agent.userLastName}:
                          </td>
                          <td
                            style={{
                              backgroundColor: '#9fddaeff',
                              color: 'black',
                              textAlign: 'right',
                              fontWeight: 'bold',
                            }}
                          >
                            {parsePrice(agent.grandTotal)}
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
          </div>
        </Col>
      </Row>

      <ModalConfirmation
        isOpen={isSendEmailsModalOpen}
        onClose={() => setIsSendEmailsModalOpen(false)}
        onConfirm={handleSendEmails}
        refetch={handleSearch}
        title='Send payroll emails'
        body={confirmationBody}
        confirmText='Send emails'
        confirmVariant='success'
      />
    </div>
  )
}
